import { DEFAULT_TICKET_HOURS, DEFAULT_WINTER_TICKET_HOURS, normalizeTicketHours, type TicketHours, type TicketHoursMode } from './ticket-hours';
export { getDefaultTicketHours } from './ticket-hours';
export type { TicketHours, TicketHoursMode } from './ticket-hours';

export interface TemporaryOverride {
  enabled: boolean;
  expiresAt: string;
}

export interface WinterMode {
  enabled: boolean;
  description: string;
}



const DEFAULT_TEMPORARY_OVERRIDE: TemporaryOverride = {
  enabled: false,
  expiresAt: "",
};

const DEFAULT_WINTER_MODE: WinterMode = {
  enabled: false,
  description: "Aktiviert die Winterzeiten",
};

let regularTicketHoursCache: TicketHours | null = null;
let winterTicketHoursCache: TicketHours | null = null;
let temporaryOverrideCache: TemporaryOverride | null = null;
let winterModeCache: WinterMode | null = null;
let cacheTimestamp = 0;
let overrideCacheTimestamp = 0;
let winterModeTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000;
const OVERRIDE_CACHE_DURATION = 5 * 1000;
const WINTER_MODE_CACHE_DURATION = 10 * 60 * 1000;

type TicketSettingsPayload = {
  ticket_hours?: TicketHours;
  ticket_hours_winter?: TicketHours;
  winter_mode?: WinterMode;
};

async function getTicketSettingsPayload(): Promise<TicketSettingsPayload | null> {
  try {
    const response = await fetch("/api/ticket-settings");
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data.settings ?? null;
  } catch (error) {
    console.error("Error fetching ticket settings:", error);
    return null;
  }
}

export async function getTicketHours(mode: TicketHoursMode = "regular"): Promise<TicketHours> {
  const now = Date.now();
  const cache = mode === "winter" ? winterTicketHoursCache : regularTicketHoursCache;
  if (cache && (now - cacheTimestamp) < CACHE_DURATION) {
    return cache;
  }

  const settings = await getTicketSettingsPayload();
  if (!settings) {
    console.warn("Failed to fetch ticket hours from API, using defaults");
    return mode === "winter" ? DEFAULT_WINTER_TICKET_HOURS : DEFAULT_TICKET_HOURS;
  }

  regularTicketHoursCache = normalizeTicketHours(settings.ticket_hours, "regular");
  winterTicketHoursCache = normalizeTicketHours(settings.ticket_hours_winter, "winter");
  cacheTimestamp = now;

  return mode === "winter" ? winterTicketHoursCache : regularTicketHoursCache;
}

export async function getActiveTicketHours(): Promise<TicketHours> {
  const winterEnabled = await isWinterModeActive();
  return getTicketHours(winterEnabled ? "winter" : "regular");
}

export async function updateTicketHours(
  newHours: Partial<TicketHours>,
  mode: TicketHoursMode = "regular"
): Promise<boolean> {
  try {
    const currentHours = await getTicketHours(mode);
    const updatedHours = { ...currentHours, ...newHours };

    const response = await fetch("/api/admin/settings", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        key: mode === "winter" ? "ticket_hours_winter" : "ticket_hours",
        value: updatedHours,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Failed to update ticket hours:", errorData.error);
      return false;
    }

    regularTicketHoursCache = null;
    winterTicketHoursCache = null;
    cacheTimestamp = 0;
    return true;
  } catch (error) {
    console.error("Error updating ticket hours:", error);
    return false;
  }
}

export async function getTemporaryOverride(): Promise<TemporaryOverride> {
  const now = Date.now();
  if (temporaryOverrideCache && (now - overrideCacheTimestamp) < OVERRIDE_CACHE_DURATION) {
    return temporaryOverrideCache;
  }

  try {
    const response = await fetch('/api/temporary-override');
    if (response.ok) {
      const data = await response.json();
      const override = data.override as TemporaryOverride || DEFAULT_TEMPORARY_OVERRIDE;
      temporaryOverrideCache = override;
      overrideCacheTimestamp = now;
      return temporaryOverrideCache;
    }
    return DEFAULT_TEMPORARY_OVERRIDE;
  } catch (error) {
    console.error('Error fetching temporary override:', error);
    return DEFAULT_TEMPORARY_OVERRIDE;
  }
}

export async function isTemporaryOverrideActive(): Promise<boolean> {
  const override = await getTemporaryOverride();
  if (!override.enabled || !override.expiresAt) return false;
  return new Date() < new Date(override.expiresAt);
}

export async function getWinterMode(): Promise<WinterMode> {
  const now = Date.now();
  if (winterModeCache && (now - winterModeTimestamp) < WINTER_MODE_CACHE_DURATION) {
    return winterModeCache;
  }

  try {
    const settings = await getTicketSettingsPayload();
    if (settings) {
      const winterMode = settings.winter_mode || DEFAULT_WINTER_MODE;
      winterModeCache = winterMode;
      winterModeTimestamp = now;
      return winterModeCache;
    }
    return DEFAULT_WINTER_MODE;
  } catch (error) {
    console.error('Error fetching winter mode:', error);
    return DEFAULT_WINTER_MODE;
  }
}

export async function isWinterModeActive(): Promise<boolean> {
  const winterMode = await getWinterMode();
  return winterMode.enabled;
}

export async function getTicketHoursWithWinter(): Promise<TicketHours> {
  return getActiveTicketHours();
}

export function clearSettingsCache(): void {
  regularTicketHoursCache = null;
  winterTicketHoursCache = null;
  temporaryOverrideCache = null;
  winterModeCache = null;
  cacheTimestamp = 0;
  overrideCacheTimestamp = 0;
  winterModeTimestamp = 0;
}

export async function getUserRole(): Promise<string | null> {
  try {
    if (typeof window !== 'undefined') {
      const response = await fetch('/api/admin/settings');
      if (response.ok) {
        const data = await response.json();
        return data.userRole || null;
      }
    }
    return null;
  } catch (error) {
    console.error('Error fetching user role:', error);
    return null;
  }
}

export function clearOverrideCache(): void {
  temporaryOverrideCache = null;
  overrideCacheTimestamp = 0;
}

/**
 * Check if a ticket type is currently outside its sale window.
 * Returns the note to show, or null if within sale window.
 */
export function getOutsideSaleNote(
  ticketType: string,
  currentHour: number,
  hours: TicketHours,
): string | null {
  let saleStart: number;
  let saleEnd: number;

  switch (ticketType) {
    case "pkwday":
      saleStart = hours.dayTicketSaleStart;
      saleEnd = hours.dayTicketSaleEnd;
      break;
    case "four":
      saleStart = hours.afternoonSaleStart;
      saleEnd = hours.afternoonSaleEnd;
      break;
    case "womo":
      saleStart = hours.womoSaleStart;
      saleEnd = hours.womoSaleEnd;
      break;
    case "caravan":
      saleStart = hours.caravanSaleStart;
      saleEnd = hours.caravanSaleEnd;
      break;
    case "pkwnight":
      saleStart = hours.pkwNightSaleStart;
      saleEnd = hours.pkwNightSaleEnd;
      break;
    default:
      return null;
  }

  const wrapsAround = saleEnd < saleStart;
  const insideSaleWindow = wrapsAround
    ? (currentHour >= saleStart || currentHour < saleEnd)
    : (currentHour >= saleStart && currentHour < saleEnd);
  if (insideSaleWindow) return null;

  if (ticketType === "womo" || ticketType === "caravan" || ticketType === "pkwnight") {
    return "Nachzahlung";
  }
  return "Bitte vor Ort bezahlen";
}
